/**
 * ChapterAppSplit — collapsible chapter-viewer / app-panel layout.
 *
 * Gives monograph and manuscript demos a first-class layout model for
 * switching between reading and running without each demo inventing its own
 * chapter-navigation shell. It composes two caller-supplied surfaces — a
 * `chapterPanel` (typically a shared `ManuscriptViewer`) and an `appPanel`
 * (the most relevant suggested app, or any app panel) — and exposes the four
 * view configurations issue #2108 asks for:
 *
 *   - `app-only`     — just the app panel in view
 *   - `chapter-only` — just the manuscript chapter panels in view
 *   - `split`        — chapters next to the suggested app panel
 *   - (the panels are arbitrary nodes, so `split` also covers "any app panel
 *      next to any chapter viewer panel")
 *
 * The mode is switchable through an accessible segmented control, and in
 * `split` view each pane carries a collapse affordance that drops to the
 * matching single-pane mode. The component is layout-only: it owns no
 * manuscript state, so it stays reusable across every demo's content.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useId, useMemo, useState } from "react";
import { resolveTheme } from "./theme.js";
import type { ManuscriptViewerTheme } from "./types.js";

export type ChapterAppSplitMode = "split" | "chapter-only" | "app-only";

export interface ChapterAppSplitProps {
  /** Reading surface shown for the chapters — typically a `<ManuscriptViewer/>`. */
  readonly chapterPanel: React.ReactNode;
  /** Application surface shown beside the chapters. */
  readonly appPanel: React.ReactNode;
  /** Label for the chapter pane in the control and pane header. Defaults to "Manuscript". */
  readonly chapterLabel?: string;
  /** Label for the app pane in the control and pane header. Defaults to "App". */
  readonly appLabel?: string;
  /** Uncontrolled initial mode. Defaults to "split". Ignored when `mode` is set. */
  readonly defaultMode?: ChapterAppSplitMode;
  /** Controlled mode. When supplied, the component does not own mode state. */
  readonly mode?: ChapterAppSplitMode;
  /** Notified whenever the mode changes, in both controlled and uncontrolled use. */
  readonly onModeChange?: (mode: ChapterAppSplitMode) => void;
  /** Dark theme tokens — defaults to the built-in manuscript palette. */
  readonly theme?: Partial<ManuscriptViewerTheme>;
  /** Which side the chapter pane sits on in `split` view. Defaults to "left". */
  readonly chapterSide?: "left" | "right";
  /**
   * Chapter-pane width fraction (0–1) in `split` view. Defaults to 0.5.
   * Clamped to a readable 0.2–0.8 range.
   */
  readonly splitChapterFraction?: number;
  /** Accessible label for the mode toolbar. Defaults to "Layout". */
  readonly toolbarLabel?: string;
}

const MODE_SEQUENCE: readonly ChapterAppSplitMode[] = ["chapter-only", "split", "app-only"];
const CHROME_AWARE_VIEWPORT_HEIGHT =
  "calc(100vh - var(--csk-hub-route-bar-height, 0px) - var(--csk-monograph-top-bar-height, 0px))";

function clampFraction(value: number): number {
  if (Number.isNaN(value)) return 0.5;
  return Math.min(0.8, Math.max(0.2, value));
}

export function ChapterAppSplit({
  chapterPanel,
  appPanel,
  chapterLabel = "Manuscript",
  appLabel = "App",
  defaultMode = "split",
  mode: controlledMode,
  onModeChange,
  theme: themeOverride,
  chapterSide = "left",
  splitChapterFraction = 0.5,
  toolbarLabel = "Layout",
}: ChapterAppSplitProps): React.JSX.Element {
  const theme = useMemo(() => resolveTheme(themeOverride), [themeOverride]);
  const baseId = useId();

  const [uncontrolledMode, setUncontrolledMode] = useState<ChapterAppSplitMode>(defaultMode);
  const isControlled = controlledMode !== undefined;
  const mode = isControlled ? controlledMode : uncontrolledMode;

  const setMode = useCallback(
    (next: ChapterAppSplitMode): void => {
      if (!isControlled) setUncontrolledMode(next);
      onModeChange?.(next);
    },
    [isControlled, onModeChange],
  );

  const chapterFraction = clampFraction(splitChapterFraction);
  const chapterRegionId = `${baseId}-chapter`;
  const appRegionId = `${baseId}-app`;

  const modeLabel: Record<ChapterAppSplitMode, string> = {
    "chapter-only": chapterLabel,
    split: "Split",
    "app-only": appLabel,
  };

  const chapterPane = (
    <Pane
      key="chapter"
      regionId={chapterRegionId}
      label={chapterLabel}
      theme={theme}
      showHeader={mode === "split"}
      // Collapsing the chapter pane leaves only the app in view.
      onCollapse={() => {
        setMode("app-only");
      }}
      collapseLabel={`Hide ${chapterLabel}`}
      flexBasis={mode === "split" ? `${(chapterFraction * 100).toFixed(2)}%` : undefined}
    >
      {chapterPanel}
    </Pane>
  );

  const appPane = (
    <Pane
      key="app"
      regionId={appRegionId}
      label={appLabel}
      theme={theme}
      showHeader={mode === "split"}
      // Collapsing the app pane leaves only the chapters in view.
      onCollapse={() => {
        setMode("chapter-only");
      }}
      collapseLabel={`Hide ${appLabel}`}
      flexBasis={mode === "split" ? `${((1 - chapterFraction) * 100).toFixed(2)}%` : undefined}
    >
      {appPanel}
    </Pane>
  );

  const orderedPanes = chapterSide === "left" ? [chapterPane, appPane] : [appPane, chapterPane];

  return (
    <div
      data-csk-chapter-app-split
      style={{
        display: "flex",
        flexDirection: "column",
        height: CHROME_AWARE_VIEWPORT_HEIGHT,
        minHeight: CHROME_AWARE_VIEWPORT_HEIGHT,
        background: theme.void,
        color: theme.text,
      }}
    >
      <div
        role="toolbar"
        aria-label={toolbarLabel}
        style={{
          display: "flex",
          gap: 4,
          alignItems: "center",
          padding: "8px 12px",
          borderBottom: `1px solid ${theme.surfaceMuted}`,
          background: theme.surface,
        }}
      >
        {MODE_SEQUENCE.map((candidate) => {
          const active = candidate === mode;
          return (
            <button
              key={candidate}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setMode(candidate);
              }}
              style={{
                background: active ? theme.accent : "transparent",
                border: `1px solid ${active ? theme.accent : theme.border}`,
                borderRadius: 8,
                color: active ? theme.void : theme.text,
                cursor: "pointer",
                fontFamily: theme.serif,
                fontSize: 12,
                fontWeight: active ? 700 : 520,
                letterSpacing: 0,
                padding: "5px 13px",
              }}
            >
              {modeLabel[candidate]}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          flex: 1,
          minHeight: 0,
        }}
      >
        {mode === "chapter-only" ? chapterPane : mode === "app-only" ? appPane : orderedPanes}
      </div>
    </div>
  );
}

interface PaneProps {
  readonly regionId: string;
  readonly label: string;
  readonly theme: ManuscriptViewerTheme;
  readonly showHeader: boolean;
  readonly onCollapse: () => void;
  readonly collapseLabel: string;
  readonly flexBasis: string | undefined;
  readonly children: React.ReactNode;
}

function Pane({
  regionId,
  label,
  theme,
  showHeader,
  onCollapse,
  collapseLabel,
  flexBasis,
  children,
}: PaneProps): React.JSX.Element {
  return (
    <section
      id={regionId}
      aria-label={label}
      style={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: flexBasis ?? "0%",
        minWidth: 0,
        overflow: "auto",
        borderRight: showHeader ? `1px solid ${theme.surfaceMuted}` : undefined,
      }}
    >
      {showHeader ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "4px 10px",
            borderBottom: `1px solid ${theme.surfaceMuted}`,
            background: theme.surface,
          }}
        >
          <span
            style={{
              fontFamily: theme.serif,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0,
              textTransform: "uppercase",
              color: theme.muted,
            }}
          >
            {label}
          </span>
          <button
            type="button"
            aria-label={collapseLabel}
            title={collapseLabel}
            onClick={onCollapse}
            style={{
              background: "transparent",
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              padding: "2px 8px",
              color: theme.text,
              cursor: "pointer",
              fontFamily: theme.serif,
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            ⤢
          </button>
        </div>
      ) : null}
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </section>
  );
}
